import { c as _c } from "react/compiler-runtime";
import React from "react";
import { ApiWaveLog } from "@/generated/models/ApiWaveLog";
import { formatNumberWithCommas, getTimeAgoShort } from "@/helpers/Helpers";
import Link from "next/link";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { SystemAdjustmentPill } from "@/components/common/SystemAdjustmentPill";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import Image from "next/image";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { isEthereumAddress, isAutoGeneratedHandle } from "@/helpers/AllowlistToolHelpers";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
interface SingleWaveDropLogProps {
  readonly log: ApiWaveLog;
  readonly creditType: ApiWaveCreditType;
}
export const SingleWaveDropLog: React.FC<SingleWaveDropLogProps> = t0 => {
  const $ = _c(23);
  const {
    log,
    creditType
  } = t0;
  const isMobile = useIsMobileScreen();
  const handleValue = log.invoker.handle ?? "";
  const shouldLimit = isEthereumAddress(handleValue) || isAutoGeneratedHandle(handleValue);
  let t1;
  if ($[0] !== isMobile || $[1] !== log.invoker.pfp) {
    t1 = log.invoker.pfp ? <Image unoptimized={true} src={getScaledImageUri(log.invoker.pfp, ImageScale.W_AUTO_H_50)} alt="User profile picture" width={isMobile ? 28 : 24} height={isMobile ? 28 : 24} className="tw-rounded-md tw-ring-1 tw-ring-white/10 tw-bg-iron-800 tw-flex-shrink-0 tw-object-contain" /> : <div className={`tw-rounded-md tw-ring-1 tw-ring-white/10 tw-bg-iron-800 tw-flex-shrink-0 ${isMobile ? "tw-size-7" : "tw-size-6"}`} />;
    $[0] = isMobile;
    $[1] = log.invoker.pfp;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const avatar = t1;
  const t2 = log.invoker.handle ?? log.invoker.id;
  const t3 = `/${log.invoker.handle}`;
  const t4 = `tw-inline-block${shouldLimit ? " tw-truncate tw-max-w-[8rem]" : ""}`;
  let t5;
  if ($[3] !== log.invoker.handle) {
    t5 = <span className="tw-text-sm tw-font-medium tw-text-iron-50 tw-transition-all tw-duration-300 desktop-hover:group-hover:tw-text-iron-300">{log.invoker.handle}</span>;
    $[3] = log.invoker.handle;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t6) {
    t7 = <Link href={t3} className="tw-no-underline tw-group desktop-hover:hover:tw-opacity-80 tw-transition-all tw-duration-300">{t6}</Link>;
    $[8] = t3;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t2 || $[12] !== t7) {
    t8 = <UserProfileTooltipWrapper user={t2}>{t7}</UserProfileTooltipWrapper>;
    $[11] = t2;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleLink = t8;
  let t9;
  if ($[14] !== avatar || $[15] !== creditType || $[16] !== handleLink || $[17] !== isMobile || $[18] !== log.contents.newVote || $[19] !== log.contents.oldVote || $[20] !== log.contents.reason || $[21] !== log.created_at) {
    t9 = <div className="tw-p-3 tw-bg-iron-900">{isMobile ? <div className="tw-flex tw-gap-3">{avatar}<div className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0"><div className="tw-flex tw-items-center tw-justify-between tw-gap-2">{handleLink}<div className="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap"><svg className="tw-w-3 tw-h-3 tw-text-iron-400" viewBox="0 0 24 24" aria-hidden="true" fill="none"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg><span className="tw-text-xs tw-font-medium tw-text-iron-400">{getTimeAgoShort(new Date(log.created_at).getTime())}</span></div></div><div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1.5 tw-mt-1"><span className="tw-text-sm tw-text-iron-400">{log.contents.oldVote === 0 ? "voted" : "changed from"}</span>{log.contents.oldVote !== 0 && <span className="tw-text-sm tw-text-iron-500 tw-whitespace-nowrap">{formatNumberWithCommas(log.contents.oldVote)} →</span>}<span className={`tw-text-sm tw-font-semibold tw-whitespace-nowrap ${log.contents.newVote > 0 ? "tw-text-green" : "tw-text-red"}`}>{formatNumberWithCommas(log.contents.newVote)} {creditType}</span>{log.contents?.reason === "CREDIT_OVERSPENT" && <SystemAdjustmentPill />}</div></div></div> : <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2"><div className="tw-flex tw-items-center tw-gap-x-2 tw-gap-y-2">{avatar}<div className="tw-inline-flex tw-items-center tw-gap-x-1 tw-gap-y-1 tw-flex-wrap">{handleLink}<span className="tw-text-sm tw-text-iron-400">{log.contents.oldVote === 0 ? "voted" : "changed from"}</span>{log.contents.oldVote !== 0 && <span className="tw-text-sm tw-text-iron-500 tw-whitespace-nowrap">{formatNumberWithCommas(log.contents.oldVote)} →</span>}<span className={`tw-text-sm tw-font-semibold tw-whitespace-nowrap ${log.contents.newVote > 0 ? "tw-text-green" : "tw-text-red"}`}>{formatNumberWithCommas(log.contents.newVote)} {creditType}</span>{log.contents?.reason === "CREDIT_OVERSPENT" && <SystemAdjustmentPill />}</div></div><div className="tw-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap"><svg className="tw-w-3.5 tw-h-3.5 tw-text-iron-400 tw-flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true" fill="none"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg><span className="tw-text-xs tw-font-medium tw-text-iron-400">{getTimeAgoShort(new Date(log.created_at).getTime())}</span></div></div>}</div>;
    $[14] = avatar;
    $[15] = creditType;
    $[16] = handleLink;
    $[17] = isMobile;
    $[18] = log.contents.newVote;
    $[19] = log.contents.oldVote;
    $[20] = log.contents.reason;
    $[21] = log.created_at;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};